import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Empty, Input, Modal, Space, Table } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { EditOutlined, SearchOutlined } from '@ant-design/icons'
import { updateSession, useAuthSession } from '../lib/session'
import { useToast } from '../contexts/ToastContext'
import { useAdvisoryMappingList, useUpdateAdvisoryMappingGroupMutation } from '../hooks/useAdvisoryMapping'
import type { AdvisoryMappingListRow } from '../types/advisoryMapping'

export default function AdvisoryMapping() {
  const navigate = useNavigate()
  const session = useAuthSession()
  const clientId = session?.currentClientId || ''
  const { addToast } = useToast()

  const defaultPageSize = session?.defaultPageSize && session.defaultPageSize > 0 ? session.defaultPageSize : 20

  const [search, setSearch] = useState('')
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<AdvisoryMappingListRow | null>(null)
  const [groupName, setGroupName] = useState('')
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: defaultPageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  })

  const listQuery = useAdvisoryMappingList(clientId)
  const updateGroupMutation = useUpdateAdvisoryMappingGroupMutation(clientId)

  const filteredRows = useMemo(() => {
    const rows = listQuery.data || []
    if (!search.trim()) return rows
    const q = search.trim().toLowerCase()
    return rows.filter((row) => row.organisationName.toLowerCase().includes(q))
  }, [listQuery.data, search])

  const columns: ColumnsType<AdvisoryMappingListRow> = [
    {
      title: (
        <Input
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          suffix={<SearchOutlined />}
          placeholder="Organisation"
          allowClear
        />
      ),
      dataIndex: 'organisationName',
      key: 'organisationName',
      render: (_value, row) => (
        <div className="flex items-center gap-2">
          <a className="clicky text-brand-700 hover:text-brand-800" onClick={() => navigate(`/advisorymapping/detailed/${row.platformType}/${row.platformId}`)}>
            {row.organisationName}
          </a>
          <a
            className="clicky text-brand-700 hover:text-brand-800"
            onClick={() => {
              setSelectedRow(row)
              setGroupName(row.group || '')
              setGroupModalOpen(true)
            }}
          >
            <EditOutlined />
          </a>
        </div>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'group',
      key: 'group',
    },
    {
      title: 'Platform',
      key: 'platform',
      render: (_value, row) => {
        if (row.platformType === 1) return 'Xero'
        if (row.platformType === 9) return 'QuickBooks'
        return ''
      },
    },
  ]

  const onSaveGroup = async () => {
    if (!selectedRow) return
    try {
      await updateGroupMutation.mutateAsync({ platformId: selectedRow.platformId, groupName: groupName.trim() })
      addToast('Group updated', 'success')
      setGroupModalOpen(false)
      setSelectedRow(null)
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to update group', 'error')
    }
  }

  if (!clientId) {
    return <Alert type="warning" showIcon message="No active client selected" />
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center pb-4">
        <h1 className="font-display text-2xl font-bold text-gray-900">Advisory Mapping</h1>
      </div>

      {listQuery.error ? (
        <div className="pb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load advisory mappings"
            description={listQuery.error instanceof Error ? listQuery.error.message : 'Unknown error'}
          />
        </div>
      ) : null}

      <Table<AdvisoryMappingListRow>
        rowKey="platformId"
        columns={columns}
        dataSource={filteredRows}
        loading={listQuery.isLoading || listQuery.isFetching}
        pagination={{
          ...pagination,
          onChange: (current, pageSize) => {
            setPagination((prev) => ({ ...prev, current, pageSize }))
            if (session && pageSize && pageSize !== session.defaultPageSize) {
              updateSession({ defaultPageSize: pageSize })
            }
          },
        }}
        locale={{ emptyText: listQuery.isLoading ? 'Loading...' : <Empty description="No advisory mappings found" /> }}
      />

      <Modal
        title="Edit Platform Group"
        open={groupModalOpen}
        onCancel={() => setGroupModalOpen(false)}
        onOk={onSaveGroup}
        okText="Save"
        confirmLoading={updateGroupMutation.isPending}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>{selectedRow?.organisationName}</div>
          <Input
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
            placeholder="Group Name"
            maxLength={100}
          />
        </Space>
      </Modal>
    </div>
  )
}
